import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Search, Download, X, IdCard, Mail, Phone, MapPin, Calendar, Car,
  ShieldCheck, Copy, ExternalLink, Loader2, User, DollarSign, Ban, CheckCircle2,
  KeyRound, UserRound, Paperclip, Upload, Trash2, FileText, Image as ImageIcon,
  BadgeCheck, Merge,
} from "lucide-react";
import { api } from "../../lib/api";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * CustomersTab — the master customer file. Aggregates every renter and
 * chauffeur client we've ever had (guests included) with lifetime totals,
 * ID verification snapshot, contact info, and one-click access to their
 * booking history. Backed by GET /api/admin/customers which merges the
 * `users` and `bookings` collections keyed by email (phone fallback).
 */
export default function CustomersTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [sortKey, setSortKey] = useState("last_seen");
  // Default to Renters — matches the top request in this back office view.
  const [segment, setSegment] = useState("renters"); // renters | chauffeured | all

  useEffect(() => {
    api.get("/admin/customers")
      .then((r) => setRows(Array.isArray(r.data) ? r.data : []))
      .catch(() => toast.error("Failed to load customers"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let out = rows;
    // Segment first — Renters view shows anyone with a rental on file,
    // Chauffeured shows anyone with a ride on file.
    if (segment === "renters") {
      out = out.filter((c) => (c.rentals_count || 0) > 0);
    } else if (segment === "chauffeured") {
      out = out.filter((c) => (c.rides_count || 0) > 0);
    }
    if (needle) {
      out = out.filter((c) => (
        (c.name || "").toLowerCase().includes(needle) ||
        (c.email || "").toLowerCase().includes(needle) ||
        (c.phone || "").toLowerCase().includes(needle) ||
        (c.dl_number || "").toLowerCase().includes(needle) ||
        (c.home_address || "").toLowerCase().includes(needle)
      ));
    }
    const sorters = {
      last_seen: (a, b) => (b.last_seen || "").localeCompare(a.last_seen || ""),
      first_seen: (a, b) => (a.first_seen || "").localeCompare(b.first_seen || ""),
      total_spent: (a, b) => (b.total_spent || 0) - (a.total_spent || 0),
      bookings_count: (a, b) => (b.bookings_count || 0) - (a.bookings_count || 0),
      name: (a, b) => (a.name || "").localeCompare(b.name || ""),
    };
    return [...out].sort(sorters[sortKey] || sorters.last_seen);
  }, [rows, q, sortKey, segment]);

  const segmentCounts = useMemo(() => ({
    all:         rows.length,
    renters:     rows.filter((c) => (c.rentals_count || 0) > 0).length,
    chauffeured: rows.filter((c) => (c.rides_count   || 0) > 0).length,
  }), [rows]);

  const stats = useMemo(() => ({
    total: rows.length,
    with_ids: rows.filter((r) => r.dl_front_file_id).length,
    lifetime_revenue: rows.reduce((s, r) => s + (r.total_spent || 0), 0),
    repeat: rows.filter((r) => (r.bookings_count || 0) > 1).length,
  }), [rows]);

  const exportCSV = () => {
    const cols = ["name","email","phone","bookings_count","rentals_count","rides_count","total_spent","dl_number","dl_state","dl_expiration","date_of_birth","home_address","first_seen","last_seen","latest_vehicle","deposit_status"];
    const esc = (v) => {
      const s = v == null ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [cols.join(",")]
      .concat(filtered.map((r) => cols.map((c) => esc(r[c])).join(",")))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `plenty-rydez-customers-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} customers`);
  };

  return (
    <div data-testid="bo-customers-tab" className="space-y-4">
      {/* Header + stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Stat label="Total customers" value={stats.total} tone="white" />
        <Stat label="Verified IDs on file" value={stats.with_ids} tone="lime" />
        <Stat label="Repeat clients" value={stats.repeat} tone="cyan" />
        <Stat label="Lifetime revenue" value={`$${stats.lifetime_revenue.toLocaleString(undefined, { maximumFractionDigits: 0 })}`} tone="gold" />
      </div>

      {/* Segment toggle — Renters vs Chauffeured. Renters is the default view. */}
      <div className="inline-flex flex-wrap gap-1 rounded-full border border-white/10 bg-black/30 p-1" role="tablist">
        {[
          { key: "renters",     label: "Renters",     icon: KeyRound,   n: segmentCounts.renters },
          { key: "chauffeured", label: "Chauffeured", icon: UserRound,  n: segmentCounts.chauffeured },
          { key: "all",         label: "All",         icon: User,       n: segmentCounts.all },
        ].map((seg) => {
          const Icon = seg.icon;
          const active = segment === seg.key;
          return (
            <button
              key={seg.key}
              type="button"
              data-testid={`bo-customers-seg-${seg.key}`}
              onClick={() => setSegment(seg.key)}
              className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs label-caps transition ${
                active
                  ? "bg-cyan/15 text-cyan ring-1 ring-cyan/40"
                  : "text-white/60 hover:text-white"
              }`}
              role="tab" aria-selected={active}
            >
              <Icon size={12} /> {seg.label}
              <span className={`ml-1 font-mono text-[10px] ${active ? "text-cyan/80" : "text-white/40"}`}>
                {seg.n}
              </span>
            </button>
          );
        })}
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            type="search"
            data-testid="bo-customers-search"
            placeholder="Search name, email, phone, DL#, address…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full bg-ink-800 border border-white/10 rounded-full pl-9 pr-4 py-2 text-sm text-white outline-none focus:border-cyan/50"
          />
        </div>
        <select
          value={sortKey}
          onChange={(e) => setSortKey(e.target.value)}
          data-testid="bo-customers-sort"
          className="bg-ink-800 border border-white/10 rounded-full px-4 py-2 text-xs label-caps text-white/70"
        >
          <option value="last_seen">Most recent</option>
          <option value="first_seen">Oldest first</option>
          <option value="total_spent">Highest spend</option>
          <option value="bookings_count">Most bookings</option>
          <option value="name">Name A–Z</option>
        </select>
        <button
          type="button"
          data-testid="bo-customers-export"
          onClick={exportCSV}
          disabled={!filtered.length}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs label-caps border border-cyan/40 text-cyan hover:bg-cyan/10 disabled:opacity-40"
        >
          <Download size={12} /> Export CSV
        </button>
      </div>

      {/* Table */}
      <div className="glass rounded-2xl overflow-hidden">
        {loading ? (
          <div className="p-10 flex items-center justify-center text-white/50 gap-2 text-sm">
            <Loader2 size={14} className="animate-spin" /> Loading customers…
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-white/50 text-sm">
            {q
              ? `No ${segment === "renters" ? "renters" : segment === "chauffeured" ? "chauffeured clients" : "customers"} match "${q}"`
              : segment === "renters"
                ? "No renters yet."
                : segment === "chauffeured"
                  ? "No chauffeured clients yet."
                  : "No customers yet."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-ink-800 text-white/50">
                <tr>
                  <th className="text-left px-4 py-3 label-caps text-[10px]">Customer</th>
                  <th className="text-left px-4 py-3 label-caps text-[10px]">Contact</th>
                  <th className="text-left px-4 py-3 label-caps text-[10px]">Bookings</th>
                  <th className="text-left px-4 py-3 label-caps text-[10px]">Spent</th>
                  <th className="text-left px-4 py-3 label-caps text-[10px]">ID</th>
                  <th className="text-left px-4 py-3 label-caps text-[10px]">Last activity</th>
                  <th className="text-right px-4 py-3 label-caps text-[10px]"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr key={c.customer_key}
                      data-testid={`bo-customer-row-${c.customer_key}`}
                      className={`border-t border-white/5 hover:bg-white/[0.02] transition cursor-pointer ${
                        c.is_blacklisted ? "bg-red-500/[0.06]" : ""
                      }`}
                      onClick={() => setSelected(c)}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="text-white">{c.name || <span className="text-white/40">—</span>}</span>
                        {c.is_blacklisted && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] label-caps bg-red-500/15 text-red-300 ring-1 ring-red-500/50">
                            <Ban size={9} /> Blocked
                          </span>
                        )}
                      </div>
                      {c.role === "staff" && <div className="text-[9px] label-caps text-burgundy">Staff</div>}
                    </td>
                    <td className="px-4 py-3 text-white/70 text-xs">
                      {c.email && <div className="truncate max-w-[180px]">{c.email}</div>}
                      {c.phone && <div className="text-white/50">{c.phone}</div>}
                    </td>
                    <td className="px-4 py-3 text-white/80">
                      {c.bookings_count}
                      <span className="text-[10px] text-white/40 ml-1">
                        ({c.rentals_count}R · {c.rides_count}C)
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gold font-medium">${(c.total_spent || 0).toFixed(2)}</td>
                    <td className="px-4 py-3">
                      {c.dl_front_file_id ? (
                        <span className="inline-flex items-center gap-1 text-lime-300 text-xs">
                          <ShieldCheck size={12} /> On file
                        </span>
                      ) : (
                        <span className="text-white/30 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-white/50 text-xs">
                      {c.last_seen ? new Date(c.last_seen).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <ExternalLink size={12} className="text-white/40 inline" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected && (
        <CustomerDrawer
          customer={selected}
          allCustomers={rows}
          onClose={() => setSelected(null)}
          onDeleted={(key) => {
            setRows((prev) => prev.filter((r) => r.customer_key !== key));
            setSelected(null);
          }}
          onMerged={(srcKey) => {
            setRows((prev) => prev.filter((r) => r.customer_key !== srcKey));
            setSelected(null);
          }}
        />
      )}
    </div>
  );
}

function Stat({ label, value, tone }) {
  const toneMap = {
    white: "text-white",
    lime: "text-lime-300",
    cyan: "text-cyan",
    gold: "text-gold",
  };
  return (
    <div className="rounded-2xl border border-white/8 bg-white/[0.03] p-3">
      <div className="label-caps text-[9px] text-white/50">{label}</div>
      <div className={`font-serif text-xl mt-0.5 ${toneMap[tone] || "text-white"}`}>{value}</div>
    </div>
  );
}

function CustomerDrawer({ customer: c, onClose, onDeleted, onMerged, allCustomers = [] }) {
  const [bookings, setBookings] = useState(null);
  const [busy, setBusy] = useState(false);
  const [blocked, setBlocked] = useState(!!c.is_blacklisted);
  const [blockReason, setBlockReason] = useState(c.blacklist_reason || "");
  const [attachments, setAttachments] = useState([]);
  const [attachLabel, setAttachLabel] = useState("");
  const [attachKind, setAttachKind]   = useState("other");
  const [uploading, setUploading]     = useState(false);
  const [merging, setMerging]         = useState(false);
  const fileInputRef = React.useRef(null);

  const loadAttachments = React.useCallback(() => {
    api.get(`/admin/customers/${encodeURIComponent(c.customer_key)}/files`)
      .then((r) => setAttachments(r.data.files || []))
      .catch(() => setAttachments([]));
  }, [c.customer_key]);

  useEffect(() => {
    loadAttachments();
  }, [loadAttachments]);
  useEffect(() => {
    // Best-effort — pull this customer's booking history from the master list.
    api.get("/admin/bookings")
      .then((r) => {
        const rows = (r.data || []).filter((b) => {
          const be = ((b.customer_email || b.email || "") || "").toLowerCase();
          const bp = (b.customer_phone || b.phone || "");
          const ce = (c.email || "").toLowerCase();
          if (ce && be === ce) return true;
          if (!ce && bp && c.phone && bp === c.phone) return true;
          return false;
        });
        setBookings(rows.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")));
      })
      .catch(() => setBookings([]));
  }, [c]);

  const copy = (label, val) => {
    if (!val) return;
    navigator.clipboard?.writeText(val).then(
      () => toast.success(`${label} copied`),
      () => toast.error("Copy failed"),
    );
  };

  const toggleBlock = async () => {
    if (busy) return;
    if (!blocked) {
      // Blocking — ask for an optional reason so future rejections tell the
      // customer why.
      const reason = window.prompt(
        `Block ${c.name || "this customer"}? They will be BARRED from submitting any new reservations. Optional reason (shown to them):`,
        blockReason || ""
      );
      if (reason === null) return; // cancelled
      setBusy(true);
      try {
        await api.post("/admin/customers/blacklist", {
          customer_key: c.customer_key,
          email: c.email, phone: c.phone,
          blocked: true, reason: reason || null,
        });
        setBlocked(true);
        setBlockReason(reason || "");
        toast.success(`${c.name || "Customer"} blocked — future reservations will be declined`);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Block failed");
      } finally { setBusy(false); }
    } else {
      if (!window.confirm(`Unblock ${c.name || "this customer"}? They will be able to book again immediately.`)) return;
      setBusy(true);
      try {
        await api.post("/admin/customers/blacklist", {
          customer_key: c.customer_key,
          email: c.email, phone: c.phone,
          blocked: false,
        });
        setBlocked(false);
        setBlockReason("");
        toast.success(`${c.name || "Customer"} unblocked`);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Unblock failed");
      } finally { setBusy(false); }
    }
  };

  const idThumbs = [
    { k: "dl_front",  label: "DL front",   file: c.dl_front_file_id },
    { k: "dl_back",   label: "DL back",    file: c.dl_back_file_id },
    { k: "selfie",    label: "Selfie",     file: c.selfie_with_dl_file_id },
    { k: "insurance", label: "Insurance",  file: c.insurance_card_file_id },
  ].filter((x) => x.file);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md p-4 flex items-start justify-end" onClick={onClose}>
      <div
        data-testid="bo-customer-drawer"
        className="bg-ink-900 border border-white/10 rounded-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="label-caps text-[10px] text-cyan mb-1">Customer profile</div>
            <h3 className="font-serif text-3xl">{c.name || "Unnamed customer"}</h3>
            <div className="text-white/50 text-xs font-mono mt-1">
              {c.user_id || c.customer_key}
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              data-testid={`bo-customer-merge-${c.customer_key}`}
              disabled={busy || merging}
              onClick={async () => {
                // Merge this customer INTO another. We ask staff to paste the
                // destination customer_key (or email/phone key like `e:x@y.com`)
                // from the customer list — this is uncommon enough that a
                // dropdown would be overkill.
                const candidates = allCustomers.filter(
                  (r) => r.customer_key && r.customer_key !== c.customer_key
                );
                const hint = candidates.slice(0, 6)
                  .map((r) => `${r.name || "—"} · ${r.customer_key}`)
                  .join("\n");
                const dst = window.prompt(
                  `Merge ${c.name || c.email || c.phone || "this customer"} INTO another customer.\n\n` +
                  `Paste the destination customer_key (survivor). Examples from your roster:\n${hint}\n\n` +
                  `The current customer will be removed and their bookings + attachments will re-attach to the destination.`,
                  ""
                );
                const dst_key = (dst || "").trim();
                if (!dst_key || dst_key === c.customer_key) return;
                if (!/^([ep]):/.test(dst_key)) {
                  toast.error("Invalid key — must start with `e:` or `p:`");
                  return;
                }
                setMerging(true);
                try {
                  const r = await api.post("/admin/customers/merge", {
                    src_key: c.customer_key, dst_key,
                  });
                  toast.success(
                    `Merged — ${r.data.bookings_moved} booking(s) and ${r.data.attachments_moved} attachment(s) moved`
                  );
                  onMerged?.(c.customer_key);
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Merge failed");
                } finally { setMerging(false); }
              }}
              className="p-2 rounded-full hover:bg-indigo-500/15 text-indigo-300 hover:text-indigo-200 disabled:opacity-40"
              title="Merge this customer INTO another"
            >
              {merging ? <Loader2 size={15} className="animate-spin" /> : <Merge size={15} />}
            </button>
            <button
              type="button"
              data-testid={`bo-customer-delete-${c.customer_key}`}
              disabled={busy}
              onClick={async () => {
                const name = c.name || c.email || c.phone || "this customer";
                if (!window.confirm(
                  `Delete ${name} from the roster?\n\n` +
                  `Their past bookings stay for financial audit, but they'll no longer appear here. Any registered account for this email will also be removed.\n\n` +
                  `This cannot be undone.`)) return;
                setBusy(true);
                try {
                  await api.delete(`/admin/customers/${encodeURIComponent(c.customer_key)}`);
                  toast.success(`${name} removed from the roster`);
                  onDeleted?.(c.customer_key);
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Delete failed");
                } finally { setBusy(false); }
              }}
              className="p-2 rounded-full hover:bg-red-500/15 text-red-300 hover:text-red-200 disabled:opacity-40"
              title="Delete customer"
            >
              <Trash2 size={15} />
            </button>
            <button onClick={onClose} className="p-2 rounded-full hover:bg-white/5"><X size={16} /></button>
          </div>
        </div>

        {/* Blacklist banner + toggle — always visible so managers can bar or
            reinstate a customer with one click from this drawer. Rows in
            the table also show a red "Blocked" pill when this is on. */}
        <div className={`rounded-2xl border p-3 mb-4 flex items-center justify-between gap-3 ${
          blocked ? "border-red-500/50 bg-red-500/10" : "border-white/10 bg-white/[0.02]"
        }`}>
          <div className="flex items-start gap-2 min-w-0">
            {blocked ? <Ban size={16} className="text-red-300 mt-0.5 shrink-0" />
                     : <CheckCircle2 size={16} className="text-lime-300 mt-0.5 shrink-0" />}
            <div className="min-w-0">
              <div className={`text-sm ${blocked ? "text-red-200" : "text-white"}`}>
                {blocked ? "BLOCKED — reservations declined" : "In good standing"}
              </div>
              {blocked && blockReason && (
                <div className="text-[11px] text-red-200/80 mt-0.5 break-words">Reason: {blockReason}</div>
              )}
              {!blocked && (
                <div className="text-[11px] text-white/50 mt-0.5">Block to auto-reject any future rental or ride from this email/phone.</div>
              )}
            </div>
          </div>
          <button
            type="button"
            data-testid={`bo-customer-block-${c.customer_key}`}
            onClick={toggleBlock}
            disabled={busy}
            className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] label-caps border transition ${
              blocked
                ? "border-lime-400/50 text-lime-300 hover:bg-lime-400/10"
                : "border-red-500/50 text-red-300 hover:bg-red-500/15"
            } disabled:opacity-50`}
          >
            {busy ? <Loader2 size={11} className="animate-spin" /> : (blocked ? <CheckCircle2 size={11} /> : <Ban size={11} />)}
            {blocked ? "Unblock" : "Block customer"}
          </button>
        </div>

        {/* Lifetime metrics */}
        <div className="grid grid-cols-3 gap-2 mb-5">
          <Stat label="Bookings" value={c.bookings_count} tone="white" />
          <Stat label="Total spent" value={`$${(c.total_spent || 0).toFixed(2)}`} tone="gold" />
          <Stat label="First seen" value={c.first_seen ? new Date(c.first_seen).toLocaleDateString() : "—"} tone="cyan" />
        </div>

        {/* Contact */}
        <Section title="Contact">
          <Row icon={Mail}  label="Email" value={c.email} onCopy={() => copy("Email", c.email)} />
          <Row icon={Phone} label="Phone" value={c.phone} onCopy={() => copy("Phone", c.phone)} />
          <Row icon={MapPin} label="Home address" value={c.home_address} onCopy={() => copy("Address", c.home_address)} />
        </Section>

        {/* Identity */}
        <Section title="Identity verification">
          <Row icon={IdCard}   label="Driver's license #" value={c.dl_number} onCopy={() => copy("DL #", c.dl_number)} />
          <Row icon={IdCard}   label="License state / expires"
               value={[c.dl_state, c.dl_expiration].filter(Boolean).join(" · ") || null} />
          <Row icon={Calendar} label="Date of birth" value={c.date_of_birth} />
          {idThumbs.length > 0 && (
            <div className="flex gap-2 flex-wrap mt-2">
              {idThumbs.map((t) => (
                <a
                  key={t.k}
                  data-testid={`bo-customer-id-${t.k}`}
                  href={`${API_BASE}/admin/uploads/${t.file}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative w-32 h-24 rounded-lg overflow-hidden border border-white/15 bg-ink-950 hover:border-cyan/60 transition"
                  title={t.label}
                >
                  <img
                    src={`${API_BASE}/admin/uploads/${t.file}`}
                    alt={t.label}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                  <div className="absolute inset-x-0 bottom-0 px-1.5 py-0.5 bg-gradient-to-t from-black/90 to-transparent text-[9px] label-caps text-white/90 truncate">
                    {t.label}
                  </div>
                </a>
              ))}
            </div>
          )}
          {idThumbs.length === 0 && (
            <div className="text-[11px] text-white/40">No ID photos on file yet — collected on the first rental.</div>
          )}
        </Section>

        {/* Booking history */}
        <Section title={`Booking history · ${bookings?.length ?? "…"}`}>
          {bookings === null ? (
            <div className="text-xs text-white/40 inline-flex items-center gap-1.5"><Loader2 size={11} className="animate-spin" /> Loading…</div>
          ) : bookings.length === 0 ? (
            <div className="text-xs text-white/40">No bookings on file for this customer.</div>
          ) : (
            <div className="space-y-1.5">
              {bookings.slice(0, 20).map((b) => (
                <div key={b.id}
                     className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg border border-white/8 bg-white/[0.02] text-xs">
                  <div className="min-w-0">
                    <div className="text-white truncate">
                      {b.type === "rental" ? "🚗" : "🕴️"} {b.vehicle_name}
                    </div>
                    <div className="text-white/50 text-[10px]">
                      {new Date(b.type === "rental" ? b.start_at : b.pickup_datetime).toLocaleString()} · {b.status}
                    </div>
                  </div>
                  <div className="text-gold shrink-0">${Number(b.amount || 0).toFixed(2)}</div>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* Attachments — any file the manager wants on this customer's file */}
        <Section title={`Attachments · ${attachments.length}`}>
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <input
              data-testid="bo-customer-attach-label"
              placeholder="Label (e.g. Passport, W-9)"
              value={attachLabel}
              onChange={(e) => setAttachLabel(e.target.value)}
              className="flex-1 min-w-[160px] rounded-lg bg-black/50 border border-white/10 text-white text-xs px-2 py-1.5"
            />
            <select
              data-testid="bo-customer-attach-kind"
              value={attachKind}
              onChange={(e) => setAttachKind(e.target.value)}
              className="rounded-lg bg-black/50 border border-white/10 text-white/70 text-xs px-2 py-1.5"
            >
              <option value="license">Driver's license</option>
              <option value="license_back">DL (back)</option>
              <option value="selfie">Selfie w/ DL</option>
              <option value="insurance">Insurance card</option>
              <option value="proof_of_address">Proof of address</option>
              <option value="passport">Passport</option>
              <option value="other">Other</option>
            </select>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.pdf,.heic,.heif"
              className="hidden"
              onChange={async (ev) => {
                const f = ev.target.files?.[0];
                ev.target.value = "";
                if (!f) return;
                if (f.size > 12 * 1024 * 1024) {
                  toast.error("File too large (12MB max)");
                  return;
                }
                setUploading(true);
                try {
                  const fd = new FormData();
                  fd.append("file", f);
                  if (attachLabel) fd.append("label", attachLabel);
                  fd.append("kind", attachKind);
                  await api.post(
                    `/admin/customers/${encodeURIComponent(c.customer_key)}/files`,
                    fd,
                    { headers: { "Content-Type": "multipart/form-data" } }
                  );
                  toast.success("File attached");
                  setAttachLabel("");
                  loadAttachments();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Upload failed");
                } finally { setUploading(false); }
              }}
            />
            <button
              type="button"
              data-testid="bo-customer-attach-upload"
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs label-caps border border-cyan/40 text-cyan hover:bg-cyan/10 disabled:opacity-50"
            >
              {uploading ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />}
              {uploading ? "Uploading…" : "Attach file"}
            </button>
          </div>

          {attachments.length === 0 ? (
            <div className="text-[11px] text-white/40">
              No attachments on file yet. Upload a license, insurance card, passport, or any document.
            </div>
          ) : (
            <div className="space-y-1.5">
              {attachments.map((a) => {
                const isImg = (a.content_type || "").startsWith("image/");
                const Icon = isImg ? ImageIcon : FileText;
                return (
                  <div key={a.id}
                       data-testid={`bo-customer-attach-row-${a.id}`}
                       className={`flex items-center justify-between gap-2 px-3 py-2 rounded-lg border text-xs ${
                         a.verified
                           ? "border-lime-400/30 bg-lime-400/[0.05]"
                           : "border-white/8 bg-white/[0.02]"
                       }`}>
                    <a
                      href={`${API_BASE}/admin/uploads/${a.file_id}`}
                      target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-2 min-w-0 flex-1 text-white/80 hover:text-white"
                      title="Open in new tab"
                    >
                      <Icon size={13} className="text-cyan shrink-0" />
                      <span className="truncate">
                        {a.label || a.original_filename || "Untitled"}
                      </span>
                      <span className="text-white/40 shrink-0 text-[10px]">
                        · {a.kind}
                      </span>
                      {a.verified && (
                        <span
                          data-testid={`bo-customer-attach-verified-badge-${a.id}`}
                          title={`Verified by ${a.verified_by || "staff"}${a.verified_at ? ` · ${new Date(a.verified_at).toLocaleDateString()}` : ""}`}
                          className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] label-caps bg-lime-400/15 text-lime-300 ring-1 ring-lime-400/40 shrink-0"
                        >
                          <BadgeCheck size={9} /> Verified
                        </span>
                      )}
                    </a>
                    <div className="text-white/40 text-[10px] shrink-0">
                      {a.uploaded_at ? new Date(a.uploaded_at).toLocaleDateString() : ""}
                    </div>
                    <button
                      type="button"
                      data-testid={`bo-customer-attach-verify-${a.id}`}
                      onClick={async () => {
                        try {
                          const r = await api.patch(
                            `/admin/customers/${encodeURIComponent(c.customer_key)}/files/${a.id}/verify`,
                            { verified: !a.verified }
                          );
                          setAttachments((prev) => prev.map((x) =>
                            x.id === a.id
                              ? { ...x, verified: r.data.verified,
                                  verified_by: r.data.verified_by,
                                  verified_at: r.data.verified_at }
                              : x
                          ));
                          toast.success(r.data.verified ? "Marked verified" : "Verification removed");
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || "Update failed");
                        }
                      }}
                      title={a.verified ? "Remove verification" : "Mark verified by staff"}
                      className={`p-1 rounded transition ${
                        a.verified
                          ? "text-lime-300 hover:bg-lime-500/15"
                          : "text-white/40 hover:text-lime-300 hover:bg-lime-500/10"
                      }`}
                    >
                      <BadgeCheck size={13} />
                    </button>
                    <button
                      type="button"
                      data-testid={`bo-customer-attach-delete-${a.id}`}
                      onClick={async () => {
                        if (!window.confirm("Remove this attachment?")) return;
                        try {
                          await api.delete(
                            `/admin/customers/${encodeURIComponent(c.customer_key)}/files/${a.id}`);
                          toast.success("Attachment removed");
                          loadAttachments();
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || "Delete failed");
                        }
                      }}
                      className="p-1 rounded hover:bg-red-500/15 text-red-300 hover:text-red-200"
                      title="Remove"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-5">
      <div className="label-caps text-[10px] text-electric mb-2">{title}</div>
      <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-3 space-y-1.5">
        {children}
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, value, onCopy }) {
  if (!value) {
    return (
      <div className="flex items-center gap-2 text-xs">
        <Icon size={12} className="text-white/40" />
        <span className="text-white/45">{label}</span>
        <span className="ml-auto text-white/25">—</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 text-xs">
      <Icon size={12} className="text-electric" />
      <span className="text-white/60">{label}</span>
      <span className="ml-auto text-white text-right truncate max-w-[65%]" title={value}>{value}</span>
      {onCopy && (
        <button
          type="button"
          onClick={onCopy}
          className="p-1 rounded hover:bg-white/10 text-white/50 hover:text-white"
          title="Copy"
        >
          <Copy size={11} />
        </button>
      )}
    </div>
  );
}
